"use client"

import { useState, useEffect } from "react"
import { Form, Button, Alert, Modal, Table } from "react-bootstrap"
import axios from "axios"

const AdminEmbroideryDesigns = () => {
  const [formData, setFormData] = useState({
    name: "",
    designCode: "",
    discount: "",
    description: "",
    category: "",
    dimensions: {
      height: "",
      width: ""
    },
    stitchDetails: {
      count: "",
      speed: ""
    },
    tags: "",
    compatibleMachines: []
  })

  const [message, setMessage] = useState("")
  const [error, setError] = useState("")
  const [showModal, setShowModal] = useState(false)
  const [designs, setDesigns] = useState([])
  const [editDesignId, setEditDesignId] = useState(null)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [designToDelete, setDesignToDelete] = useState(null)
  const [selectedFiles, setSelectedFiles] = useState([])
  const [previewUrls, setPreviewUrls] = useState([])
  const [zipFile, setZipFile] = useState(null)
  const [zipFileName, setZipFileName] = useState('')
  
  // Machine management states
  const [showMachineModal, setShowMachineModal] = useState(false)
  const [machineFormData, setMachineFormData] = useState({
    name: "",
    format: "",
    price: "30"
  })
  const [availableMachines, setAvailableMachines] = useState([])
  const [selectedMachines, setSelectedMachines] = useState([])

  useEffect(() => {
    fetchDesigns()
    fetchMachines()
  }, [])

  const fetchMachines = async () => {
    try {
      const response = await axios.get("https://madhusewingmachines.com/api/machines/active")
      if (response.data.success) {
        setAvailableMachines(response.data.machines)
      }
    } catch (error) {
      console.error("Error fetching machines:", error)
    }
  }

  const fetchDesigns = async () => {
    try {
      const response = await axios.get("https://madhusewingmachines.com/api/embroidery-designs")
      if (response.data.success) {
        setDesigns(response.data.designs)
      }
    } catch (error) {
      console.error("Error fetching designs:", error)
      setError("Failed to fetch designs.")
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target

    if (name.startsWith("dimensions.")) {
      const field = name.split(".")[1]
      setFormData({
        ...formData,
        dimensions: {
          ...formData.dimensions,
          [field]: value
        }
      })
    } else if (name.startsWith("stitchDetails.")) {
      const field = name.split(".")[1]
      setFormData({
        ...formData,
        stitchDetails: {
          ...formData.stitchDetails,
          [field]: value
        }
      })
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : value
      })
    }
  }

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files || [])
    const combinedFiles = [...selectedFiles, ...newFiles]
    const limitedFiles = combinedFiles.slice(0, 5)
    
    setSelectedFiles(limitedFiles)
    const urls = limitedFiles.map((file) => URL.createObjectURL(file))
    setPreviewUrls(urls)
    e.target.value = ''
  }

  const handleRemoveImage = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index)
    const newUrls = previewUrls.filter((_, i) => i !== index)
    setSelectedFiles(newFiles)
    setPreviewUrls(newUrls)
  }

  const handleZipFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.type === 'application/zip' || file.type === 'application/x-zip-compressed' || file.name.endsWith('.zip')) {
        setZipFile(file)
        setZipFileName(file.name)
      } else {
        setError('Please select a valid ZIP file')
        e.target.value = ''
      }
    }
  }

  const handleRemoveZipFile = () => {
    setZipFile(null)
    setZipFileName('')
  }

  const handleMachineChange = (e) => {
    const { name, value } = e.target
    setMachineFormData({
      ...machineFormData,
      [name]: value
    })
  }

  const handleAddMachine = async () => {
    if (!machineFormData.name || !machineFormData.format || !machineFormData.price) {
      setError("Please fill all machine fields")
      return
    }

    try {
      const response = await axios.post("https://madhusewingmachines.com/api/machines", {
        ...machineFormData,
        isActive: true
      })
      
      if (response.data.success) {
        setMessage("Machine added successfully!")
        fetchMachines()
        setMachineFormData({ name: "", format: "", price: "30" })
        setShowMachineModal(false)
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to add machine")
    }
  }

  const handleMachineToggle = (machineId) => {
    setSelectedMachines(prev => {
      if (prev.includes(machineId)) {
        return prev.filter(id => id !== machineId)
      } else {
        return [...prev, machineId]
      }
    })
  }

  const resetForm = () => {
    setFormData({
      name: "",
      designCode: "",
      discount: "",
      description: "",
      category: "",
      dimensions: {
        height: "",
        width: ""
      },
      stitchDetails: {
        count: "",
        speed: ""
      },
      tags: "",
      compatibleMachines: []
    })
    setEditDesignId(null)
    setSelectedFiles([])
    setPreviewUrls([])
    setSelectedMachines([])
    setZipFile(null)
    setZipFileName('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!selectedFiles || selectedFiles.length === 0) {
      setError("Please select at least one image for the design.")
      return
    }
    
    try {
      const formDataToSend = new FormData()
      
      formDataToSend.append('name', formData.name)
      formDataToSend.append('designCode', formData.designCode)
      formDataToSend.append('discount', formData.discount)
      formDataToSend.append('description', formData.description)
      formDataToSend.append('category', formData.category)
      
      const tagsArray = formData.tags.split(",").map((tag) => tag.trim())
      formDataToSend.append('tags', JSON.stringify(tagsArray))
      formDataToSend.append('dimensions', JSON.stringify(formData.dimensions))
      formDataToSend.append('stitchDetails', JSON.stringify(formData.stitchDetails))
      
      // Add compatible machines
      const compatibleMachinesData = selectedMachines.map(machineId => {
        const machine = availableMachines.find(m => m._id === machineId)
        return {
          name: machine.name,
          format: machine.format,
          price: machine.price
        }
      })
      formDataToSend.append('compatibleMachines', JSON.stringify(compatibleMachinesData))
      
      // Append image files
      selectedFiles.forEach((file) => {
        formDataToSend.append('images', file)
      })

      // Append ZIP file if selected
      if (zipFile) {
        formDataToSend.append('designFile', zipFile)
      }

      if (editDesignId) {
        await axios.put(`https://madhusewingmachines.com/api/embroidery-designs/${editDesignId}`, {
          ...formData,
          tags: tagsArray,
          compatibleMachines: compatibleMachinesData
        })
        setMessage("Design updated successfully!")
      } else {
        await axios.post("https://madhusewingmachines.com/api/embroidery-designs", formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        setMessage("Design added successfully!")
      }
      fetchDesigns()
      resetForm()
      setShowModal(false)
    } catch (error) {
      console.error("Error details:", error.response?.data || error.message)
      setError(error.response?.data?.message || "Failed to save design. Please check the data and try again.")
    }
  }

  const handleEdit = (design) => {
    setFormData({
      ...design,
      tags: design.tags ? design.tags.join(", ") : ""
    })
    // Set selected machines if editing
    if (design.compatibleMachines && design.compatibleMachines.length > 0) {
      const machineIds = design.compatibleMachines.map(cm => {
        const machine = availableMachines.find(m => m.name === cm.name)
        return machine?._id
      }).filter(Boolean)
      setSelectedMachines(machineIds)
    }
    setEditDesignId(design._id)
    setShowModal(true)
  }

  const handleDeleteClick = (designId) => {
    setDesignToDelete(designId)
    setShowDeleteConfirmation(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`https://madhusewingmachines.com/api/embroidery-designs/${designToDelete}`)
      setMessage("Design deleted successfully!")
      fetchDesigns()
    } catch (error) {
      setError("Failed to delete design.")
      console.error(error)
    } finally {
      setShowDeleteConfirmation(false)
      setDesignToDelete(null)
    }
  }

  return (
    <div className="container">
      <h1 className="text-center">Admin Panel - Embroidery Designs</h1>
      {message && (
        <Alert variant="success" className="text-center">
          {message}
        </Alert>
      )}
      {error && (
        <Alert variant="danger" className="text-center">
          {error}
        </Alert>
      )}

      <div className="mb-3">
        <Button variant="primary" onClick={() => setShowModal(true)}>
          Add Design
        </Button>
      </div>

      {/* Add/Edit Design Modal */}
      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false)
          resetForm()
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{editDesignId ? "Edit Design" : "Add Design"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Design Code</Form.Label>
              <Form.Control 
                type="text" 
                name="designCode" 
                value={formData.designCode} 
                onChange={handleChange} 
                required 
                placeholder="e.g., JC1869"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control 
                type="text" 
                name="name" 
                value={formData.name} 
                onChange={handleChange} 
                required 
                placeholder="e.g., Mango Buti"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Discount</Form.Label>
              <Form.Control 
                type="text" 
                name="discount" 
                value={formData.discount} 
                onChange={handleChange} 
                placeholder="e.g., 20% OFF"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Control 
                type="text" 
                name="category" 
                value={formData.category} 
                onChange={handleChange} 
                placeholder="e.g., Floral, Traditional"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Images (up to 5) - Selected: {selectedFiles.length}/5</Form.Label>
              <div className="d-flex gap-2 mb-2">
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={() => document.getElementById('designImageInput').click()}
                  disabled={selectedFiles.length >= 5}
                >
                  {selectedFiles.length === 0 ? 'Select Images' : 'Add More Images'}
                </Button>
                {selectedFiles.length > 0 && (
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => {
                      setSelectedFiles([])
                      setPreviewUrls([])
                    }}
                  >
                    Clear All
                  </Button>
                )}
              </div>
              <input
                id="designImageInput"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                multiple
                style={{ display: 'none' }}
              />
              <Form.Text className="text-muted d-block">
                {selectedFiles.length >= 5 
                  ? "Maximum 5 images reached." 
                  : `You can add ${5 - selectedFiles.length} more image(s).`}
              </Form.Text>

              {previewUrls.length > 0 && (
                <>
                  <h6 className="mt-3">Selected Images Preview</h6>
                  <div className="d-flex flex-wrap mt-2">
                    {previewUrls.map((url, index) => (
                      <div key={`preview-${index}`} className="position-relative me-2 mb-2">
                        <img
                          src={url}
                          alt={`Preview ${index + 1}`}
                          className="img-thumbnail"
                          style={{ width: "100px", height: "100px", objectFit: "cover" }}
                        />
                        <Button
                          variant="danger"
                          size="sm"
                          className="position-absolute top-0 end-0 rounded-circle p-0"
                          style={{ width: "24px", height: "24px" }}
                          onClick={() => handleRemoveImage(index)}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Design File (ZIP)</Form.Label>
              <div className="d-flex gap-2 mb-2">
                <Button 
                  variant="outline-success" 
                  size="sm"
                  onClick={() => document.getElementById('zipFileInput').click()}
                >
                  {zipFile ? 'Change ZIP File' : 'Upload ZIP File'}
                </Button>
                {zipFile && (
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={handleRemoveZipFile}
                  >
                    Remove ZIP
                  </Button>
                )}
              </div>
              <input
                id="zipFileInput"
                type="file"
                accept=".zip,application/zip,application/x-zip-compressed"
                onChange={handleZipFileSelect}
                style={{ display: 'none' }}
              />
              <Form.Text className="text-muted d-block">
                Upload the embroidery design files as a ZIP archive. This will be sent to customers after purchase.
              </Form.Text>
              {zipFileName && (
                <div className="mt-2 p-2 bg-light border rounded">
                  <small>
                    <strong>Selected file:</strong> {zipFileName}
                  </small>
                </div>
              )}
            </Form.Group>

            <h5 className="mt-4">Dimensions</h5>

            <Form.Group className="mb-3">
              <Form.Label>Height</Form.Label>
              <Form.Control
                type="text"
                name="dimensions.height"
                value={formData.dimensions.height}
                onChange={handleChange}
                placeholder="e.g., 5 inch"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Width</Form.Label>
              <Form.Control
                type="text"
                name="dimensions.width"
                value={formData.dimensions.width}
                onChange={handleChange}
                placeholder="e.g., 4 inch"
              />
            </Form.Group>

            <h5 className="mt-4">Stitch Details</h5>

            <Form.Group className="mb-3">
              <Form.Label>Stitch Count</Form.Label>
              <Form.Control
                type="number"
                name="stitchDetails.count"
                value={formData.stitchDetails.count}
                onChange={handleChange}
                placeholder="e.g., 11000"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Embroidery Speed</Form.Label>
              <Form.Control
                type="text"
                name="stitchDetails.speed"
                value={formData.stitchDetails.speed}
                onChange={handleChange}
                placeholder="e.g., 350 RPM"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Tags (comma-separated)</Form.Label>
              <Form.Control 
                type="text" 
                name="tags" 
                value={formData.tags} 
                onChange={handleChange} 
                placeholder="e.g., buti, butta, bunch, mango"
              />
            </Form.Group>

            <h5 className="mt-4">Compatible Machines</h5>
            <div className="mb-3">
              <Button 
                variant="success" 
                size="sm" 
                onClick={() => setShowMachineModal(true)}
                className="mb-3"
              >
                + Add New Machine
              </Button>
              
              {availableMachines.length === 0 ? (
                <Alert variant="info">No machines available. Add a machine first.</Alert>
              ) : (
                <div>
                  <p className="text-muted small">Select machines compatible with this design:</p>
                  {availableMachines.map((machine) => (
                    <Form.Check
                      key={machine._id}
                      type="checkbox"
                      id={`machine-${machine._id}`}
                      label={`${machine.name} - ${machine.format} (₹${machine.price})`}
                      checked={selectedMachines.includes(machine._id)}
                      onChange={() => handleMachineToggle(machine._id)}
                      className="mb-2"
                    />
                  ))}
                  {selectedMachines.length > 0 && (
                    <small className="text-success d-block mt-2">
                      ✓ {selectedMachines.length} machine(s) selected
                    </small>
                  )}
                </div>
              )}
            </div>

            <Button variant="primary" type="submit" className="mt-3">
              {editDesignId ? "Update Design" : "Add Design"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirmation} onHide={() => setShowDeleteConfirmation(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this design?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirmation(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Machine Modal */}
      <Modal show={showMachineModal} onHide={() => setShowMachineModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Machine</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Machine Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={machineFormData.name}
                onChange={handleMachineChange}
                placeholder="e.g., DST-BERNINA-14x8"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Format</Form.Label>
              <Form.Control
                type="text"
                name="format"
                value={machineFormData.format}
                onChange={handleMachineChange}
                placeholder="e.g., DST, JEF, PES"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Price (₹)</Form.Label>
              <Form.Control
                type="number"
                name="price"
                value={machineFormData.price}
                onChange={handleMachineChange}
                required
                min="0"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMachineModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddMachine}>
            Add Machine
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Designs Table */}
      <h3 className="mt-5">Designs List</h3>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Design Code</th>
            <th>Name</th>
            <th>Machine Prices</th>
            <th>Discount</th>
            <th>Category</th>
            <th>Images</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {designs.map((design) => (
            <tr key={design._id}>
              <td>{design.designCode}</td>
              <td>{design.name}</td>
              <td>
                {design.compatibleMachines && design.compatibleMachines.length > 0 ? (
                  <div>
                    {design.compatibleMachines.map((machine, idx) => (
                      <div key={idx} style={{ fontSize: '0.85rem' }}>
                        {machine.name}: ₹{machine.price}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted">No machines</span>
                )}
              </td>
              <td>{design.discount || '-'}</td>
              <td>{design.category || '-'}</td>
              <td>
                {design.images && design.images.slice(0, 3).map((image, idx) => (
                  <img
                    key={`${design._id}-img-${idx}`}
                    src={`https://madhusewingmachines.com/uploads/${image.url}`}
                    alt={`${design.name} ${idx}`}
                    className="img-thumbnail me-2"
                    style={{ width: "50px", height: "50px", objectFit: "cover" }}
                  />
                ))}
                {design.images && design.images.length > 3 && `+${design.images.length - 3} more`}
              </td>
              <td>
                <Button variant="warning" size="sm" onClick={() => handleEdit(design)} className="me-2">
                  Edit
                </Button>
                <Button variant="danger" size="sm" onClick={() => handleDeleteClick(design._id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

export default AdminEmbroideryDesigns
